// Copy normalisation for client-facing SEO output - strip the "AI writing" / provenance tells so a
// designer can hand the summary or the PDF to a client without a second thought.
//
// Two jobs:
//   1. DASHES - figure/en/em dash + horizontal bar -> a plain hyphen (matches the analytics
//      normalizeDashes rule in netlify/functions/_lib/analytics/narrative.ts, so the two outputs
//      agree).
//   2. INVISIBLE UNICODE - zero-width chars, word-joiner, BOM, soft-hyphen (removed) and exotic
//      spaces / line separators (-> a normal space). These are the characters behind the "AI
//      watermark" chatter; scrubbing to plain ASCII whitespace removes any stray fingerprint.
// Idempotent (cleaning already-clean text is a no-op), so it's safe to apply at more than one point.

import type { SeoClientSummary } from '../components/SeoSummary';

export function cleanCopy(s: string): string {
  if (typeof s !== 'string') return s;
  return s
    .normalize('NFC')
    .replace(/[\u2012\u2013\u2014\u2015]/g, '-') // figure/en/em dash + horizontal bar -> hyphen
    .replace(/[\u200B\u200C\u200D\u2060\uFEFF\u00AD]/g, '') // zero-width / word-joiner / BOM / soft-hyphen -> remove
    .replace(/[\u00A0\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]/g, ' ') // exotic spaces / separators -> normal space
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
}

const opt = (x: string | null | undefined): string | null | undefined =>
  typeof x === 'string' ? cleanCopy(x) : x;

/** Deep-normalise the model-authored PROSE fields of an SEO summary (URLs / paths / enums left as-is). */
export function cleanSeoSummary(s: SeoClientSummary): SeoClientSummary {
  return {
    ...s,
    businessName: cleanCopy(s.businessName ?? ''),
    positioningHeadline: cleanCopy(s.positioningHeadline ?? ''),
    llmsSummary: cleanCopy(s.llmsSummary ?? ''),
    areaServed: (s.areaServed ?? []).map((a) => cleanCopy(a)),
    keywords: (s.keywords ?? []).map((k) => ({ ...k, term: cleanCopy(k.term), intent: opt(k.intent) ?? null })),
    competitors: (s.competitors ?? []).map((c) => ({
      ...c,
      name: cleanCopy(c.name),
      note: c.note != null ? cleanCopy(c.note) : c.note,
    })),
    pages: (s.pages ?? []).map((p) => ({ ...p, title: cleanCopy(p.title), meta: cleanCopy(p.meta) })),
    compliance: s.compliance
      ? {
          ...s.compliance,
          posture: cleanCopy(s.compliance.posture ?? ''),
          enquiriesGoTo: cleanCopy(s.compliance.enquiriesGoTo ?? ''),
          visitorRegion: cleanCopy(s.compliance.visitorRegion ?? ''),
          adsOrPixels: opt(s.compliance.adsOrPixels) ?? undefined,
          existingPrivacyPolicy: opt(s.compliance.existingPrivacyPolicy) ?? undefined,
        }
      : s.compliance,
    notes: s.notes != null ? cleanCopy(s.notes) : s.notes,
  };
}
